// src/pages/business/orders/BusinessOrdersPage.tsx
// Enhanced version mimicking mobile OrdersScreen with web adaptations

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ShoppingCart, Calendar, CreditCard, Clock,
  CheckCircle, Settings, Package, CheckCheck, XCircle,
  HelpCircle, Globe, Loader2, Image as ImageIcon, RefreshCw,
  ChevronRight,
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { getImageUrl } from '@/config/media';
import { OrderDetailPane } from './OrderDetailPane';

type OrderStatus = 'pending' | 'confirmed' | 'processing' | 'ready' | 'delivered' | 'cancelled';
const FILTER_TABS: (OrderStatus | 'all')[] = ['all', 'pending', 'confirmed', 'processing', 'ready', 'delivered'];

const statusStyle = (s: OrderStatus) => {
  switch (s) {
    case 'pending':    return { color: '#f39c12', bgColor: 'rgba(243,156,18,0.12)', Icon: Clock };
    case 'confirmed':  return { color: '#3498db', bgColor: 'rgba(52,152,219,0.12)', Icon: CheckCircle };
    case 'processing': return { color: '#9b59b6', bgColor: 'rgba(155,89,182,0.12)', Icon: Settings };
    case 'ready':      return { color: '#1abc9c', bgColor: 'rgba(26,188,156,0.12)', Icon: Package };
    case 'delivered':  return { color: '#27ae60', bgColor: 'rgba(39,174,96,0.12)', Icon: CheckCheck };
    case 'cancelled':  return { color: '#e74c3c', bgColor: 'rgba(231,76,60,0.12)', Icon: XCircle };
    default:           return { color: '#95a5a6', bgColor: 'rgba(149,165,166,0.12)', Icon: HelpCircle };
  }
};

// ── Order Card ──────────────────────────────────────────────────────────────────
const OrderCard: React.FC<{ item: any; onPress: () => void; isSelected: boolean }> = ({ item, onPress, isSelected }) => {
  const { color, bgColor, Icon } = statusStyle(item.order_status);
  const firstItem = item.customer_order_items?.[0];
  const itemCount = item.customer_order_items?.length || 0;

  return (
    <button
      onClick={onPress}
      className={`w-full text-left rounded-xl p-4 mb-4 transition-all ${
        isSelected 
          ? 'bg-primary/5 dark:bg-primary/10 shadow-md ring-1 ring-primary/50' 
          : 'bg-light-card dark:bg-dark-card shadow-sm hover:shadow-md'
      }`}
      style={{ borderLeft: `5px solid ${color}` }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          {firstItem?.product_image_url ? (
            <img src={getImageUrl(firstItem.product_image_url)} alt="" className="w-[50px] h-[50px] rounded-lg object-cover flex-shrink-0" />
          ) : (
            <div className="w-[50px] h-[50px] rounded-lg flex items-center justify-center flex-shrink-0 bg-light-bg dark:bg-dark-bg">
              <ImageIcon size={20} style={{ color: '#94A3B8' }} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-bold truncate" style={{ fontSize: 15, color: '#0F172A' }}>{item.order_number}</p>
            <p className="mt-0.5 truncate" style={{ fontSize: 14, color: '#64748B' }}>
              {item.profiles?.full_name || item.customer_name || 'Customer'}
            </p>
            {item.order_source === 'website' && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full mt-1 text-[9px] font-bold uppercase" style={{ backgroundColor: 'rgba(14,165,233,0.12)', color: '#0EA5E9' }}>
                <Globe size={10} />Website
              </span>
            )}
            <p className="mt-0.5" style={{ fontSize: 12, color: '#64748B' }}>{itemCount} item{itemCount !== 1 ? 's' : ''}</p>
          </div>
        </div>

        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold flex-shrink-0" style={{ backgroundColor: bgColor, color: color }}>
          <Icon size={14} />
          {item.order_status.charAt(0).toUpperCase() + item.order_status.slice(1)}
        </span>
      </div>

      <div className="flex items-center justify-between pt-2.5 border-t border-light-border dark:border-dark-border">
        <span className="flex items-center gap-1.5 text-[12px]" style={{ color: '#64748B' }}>
          <CreditCard size={16} style={{ color: '#64748B' }} />
          KES {Number(item.total_amount).toLocaleString()}
        </span>
        <span className="flex items-center gap-1.5 text-[12px]" style={{ color: '#64748B' }}>
          <CreditCard size={16} style={{ color: '#64748B' }} />
          {item.payment_method?.replace('_', ' ').toUpperCase()}
        </span>
        <span className="flex items-center gap-1.5 text-[12px]" style={{ color: '#64748B' }}>
          <Calendar size={16} style={{ color: '#64748B' }} />
          {new Date(item.created_at).toLocaleDateString()}
        </span>
        <ChevronRight size={16} style={{ color: '#94A3B8' }} />
      </div>
    </button>
  );
};

// ── Screen ────────────────────────────────────────────────────────────────────
export const BusinessOrdersPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'all' | OrderStatus>('all');
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({ total_orders: 0, pending_orders: 0, total_revenue: 0 });
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // Resizable sidebar state
  const [sidebarWidth, setSidebarWidth] = useState(400);
  const isDragging = useRef(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const newWidth = document.body.clientWidth - e.clientX;
      setSidebarWidth(Math.max(300, Math.min(newWidth, 800)));
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = 'default';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
  };

  const fetchOrders = useCallback(async () => {
    try {
      const params = activeTab !== 'all' ? { status: activeTab } : {};
      const { data } = await apiClient.get(`/businesses/${businessId}/orders`, { params });
      setOrders(data.orders || []);
    } catch { /* silent */ }
    finally { setLoading(false); setRefreshing(false); }
  }, [businessId, activeTab]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/businesses/${businessId}/orders/stats`);
      setStats(data);
    } catch { /* silent */ }
  }, [businessId]);

  useEffect(() => { fetchOrders(); fetchStats(); }, [fetchOrders, fetchStats]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchOrders();
    fetchStats();
  };

  const handleOrderPress = (order: any) => {
    setSelectedOrderId(order.id);
  };

  return (
    <div className="h-screen flex flex-col bg-light-bg dark:bg-dark-bg overflow-hidden">
      {/* ── Header ── */}
      <div className="flex-shrink-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="w-full flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(`/business/${businessId}`)} className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
              <ArrowLeft size={24} style={{ color: '#4F46E5' }} />
            </button>
            <div>
              <p className="font-bold text-lg" style={{ color: '#4F46E5' }}>Product Orders</p>
              <p className="text-xs" style={{ color: '#64748B' }}>
                {stats.pending_orders} pending · KES {Number(stats.total_revenue).toLocaleString()} revenue
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/business/${businessId}/bookings`)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border transition-colors"
          >
            <Calendar size={20} style={{ color: '#4F46E5' }} />
            <span className="font-semibold text-sm" style={{ color: '#4F46E5' }}>Services</span>
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* ── Main List Column ── */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          {/* Filter tabs */}
          <div className="flex-shrink-0 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
            <div className="flex overflow-x-auto scrollbar-hide px-4 py-3 gap-2">
              {FILTER_TABS.map(tab => {
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className="flex-shrink-0 px-4 py-2 rounded-full font-semibold text-sm transition-all"
                    style={{
                      backgroundColor: isActive ? '#4F46E5' : 'transparent',
                      color: isActive ? '#fff' : '#4F46E5',
                      border: isActive ? 'none' : '1px solid #4F46E5',
                    }}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-4 pt-4 pb-24 scrollbar-thin">
            {loading ? (
              <div className="flex justify-center py-24"><Loader2 size={40} className="animate-spin" style={{ color: '#4F46E5' }} /></div>
            ) : orders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                <ShoppingCart size={64} style={{ color: '#94A3B8' }} />
                <p className="font-semibold text-lg" style={{ color: '#0F172A' }}>No orders found</p>
                <p style={{ fontSize: 14, color: '#64748B' }}>Orders will appear here when customers checkout</p>
                <button
                  onClick={onRefresh}
                  disabled={refreshing}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold bg-primary hover:bg-primary/90 transition-colors"
                >
                  <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                  Refresh
                </button>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto space-y-1">
                {orders.map(order => (
                  <OrderCard 
                    key={order.id} 
                    item={order} 
                    isSelected={selectedOrderId === order.id}
                    onPress={() => handleOrderPress(order)} 
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Resizable Sidebar ── */}
        {selectedOrderId && (
          <>
            <div
              className="w-1 cursor-col-resize hover:bg-primary/50 active:bg-primary bg-light-border dark:bg-dark-border transition-colors flex-shrink-0 z-50"
              onMouseDown={handleMouseDown}
            />
            <div style={{ width: sidebarWidth }} className="flex-shrink-0 bg-light-card dark:bg-dark-card shadow-[-4px_0_15px_rgba(0,0,0,0.05)] z-40 relative">
              <OrderDetailPane 
                businessId={businessId!} 
                orderId={selectedOrderId} 
                onClose={() => setSelectedOrderId(null)} 
                onStatusUpdated={() => {
                  fetchOrders();
                  fetchStats();
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BusinessOrdersPage;